/**
 * JSON Editor Online — Full-Featured Client-Side Suite
 * Replicating jsoneditoronline.org capabilities:
 * - Dual-pane workspace (Left / Right) with sync, swap, and visual diff
 * - Code Mode, Tree Mode, Table Mode
 * - One-click JSON Repair (unquoted keys, single quotes, trailing commas, Python literals, comments)
 * - Query / Transform engine with presets
 * - CSV Import / Export & File Drag-and-Drop
 * - Zero server latency, 100% client-side privacy
 */

(function () {
  'use strict';

  // Sample data sets
  const SAMPLES = {
    users: {
      "apiVersion": "2.4",
      "environment": "production",
      "users": [
        {
          "id": 101,
          "name": "Sarah Connor",
          "email": "s.connor@cyberdyne.io",
          "role": "Lead Architect",
          "verified": true,
          "skills": ["TypeScript", "GraphQL", "Rust"],
          "metrics": { "prsMerged": 142, "score": 98.6 }
        },
        {
          "id": 102,
          "name": "John Matrix",
          "email": "matrix@commando.dev",
          "role": "Security Engineer",
          "verified": true,
          "skills": ["Kubernetes", "Go", "PenTesting"],
          "metrics": { "prsMerged": 89, "score": 94.2 }
        },
        {
          "id": 103,
          "name": "Kyle Reese",
          "email": "reese@resistance.net",
          "role": "Frontend Specialist",
          "verified": false,
          "skills": ["React", "CSS Architecture", "WebAssembly"],
          "metrics": { "prsMerged": 34, "score": 87.5 }
        }
      ],
      "pagination": {
        "page": 1,
        "perPage": 20,
        "totalRecords": 3
      }
    },
    products: [
      { "sku": "TECH-001", "name": "Mechanical Keyboard Pro", "price": 149.99, "category": "Peripherals", "inStock": true, "rating": 4.8 },
      { "sku": "TECH-002", "name": "Ultra-Wide 34\" Monitor", "price": 699.00, "category": "Displays", "inStock": true, "rating": 4.9 },
      { "sku": "TECH-003", "name": "Ergonomic Mesh Chair", "price": 420.50, "category": "Furniture", "inStock": false, "rating": 4.7 },
      { "sku": "TECH-004", "name": "Noise Cancelling Headphones", "price": 289.00, "category": "Audio", "inStock": true, "rating": 4.6 },
      { "sku": "TECH-005", "name": "USB-C Dual 4K Dock", "price": 179.95, "category": "Accessories", "inStock": true, "rating": 4.5 }
    ],
    config: {
      "appName": "SkyNet Orchestrator",
      "version": "4.1.0-rc3",
      "debug": false,
      "cluster": {
        "nodes": 8,
        "region": "us-east-1",
        "zones": ["us-east-1a", "us-east-1b", "us-east-1c"]
      },
      "rateLimiting": {
        "enabled": true,
        "requestsPerMinute": 1200,
        "whitelist": ["127.0.0.1", "10.0.0.0/8"]
      },
      "database": {
        "primaryHost": "db-cluster-prod.internal",
        "port": 5432,
        "ssl": true,
        "maxConnections": 250
      }
    }
  };

  // Toast notification helper
  function showToast(msg, icon = '✓') {
    const existing = document.querySelector('.je-toast');
    if (existing) existing.remove();
    const toast = document.createElement('div');
    toast.className = 'je-toast';
    toast.innerHTML = `<span>${icon}</span> <span>${msg}</span>`;
    document.body.appendChild(toast);
    setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transition = 'opacity 0.3s ease';
      setTimeout(() => toast.remove(), 300);
    }, 3200);
  }

  /* ─────────────────────────────────────────────────────────────────
     JSON REPAIR ENGINE
     Fixes: trailing commas, single quotes, unquoted keys, python literals,
     comments, smart quotes
     ───────────────────────────────────────────────────────────────── */
  function repairJSON(rawText) {
    let fixes = [];
    let text = rawText;

    // 1. Strip comments (// ... and /* ... */)
    const commentRegex = /\/\*[\s\S]*?\*\/|\/\/.*/g;
    if (commentRegex.test(text)) {
      text = text.replace(commentRegex, '');
      fixes.push('Removed JavaScript comments');
    }

    // 2. Replace smart/curly quotes
    if (/[\u2018\u2019\u201C\u201D]/.test(text)) {
      text = text.replace(/[\u2018\u2019]/g, "'").replace(/[\u201C\u201D]/g, '"');
      fixes.push('Normalized smart quotes');
    }

    // 3. Replace Python constants (None, True, False)
    if (/\b(None|True|False)\b/.test(text)) {
      text = text.replace(/\bNone\b/g, 'null')
                 .replace(/\bTrue\b/g, 'true')
                 .replace(/\bFalse\b/g, 'false');
      fixes.push('Converted Python literals (None/True/False)');
    }

    // 4. Convert single-quoted keys and strings to double-quotes
    // Match 'key': or 'string'
    const singleQuoteKeyRegex = /'([^'\\]*(?:\\.[^'\\]*)*)'(?=\s*:)/g;
    if (singleQuoteKeyRegex.test(text)) {
      text = text.replace(singleQuoteKeyRegex, '"$1"');
      fixes.push('Converted single-quoted keys to double quotes');
    }
    const singleQuoteValRegex = /:\s*'([^'\\]*(?:\\.[^'\\]*)*)'/g;
    if (singleQuoteValRegex.test(text)) {
      text = text.replace(singleQuoteValRegex, ': "$1"');
      fixes.push('Converted single-quoted strings to double quotes');
    }

    // 5. Wrap unquoted object keys in double quotes
    // e.g. { name: "val", age_count: 12 }
    const unquotedKeyRegex = /([{,]\s*)([a-zA-Z_$][a-zA-Z0-9_$-]*)\s*:/g;
    if (unquotedKeyRegex.test(text)) {
      text = text.replace(unquotedKeyRegex, '$1"$2":');
      fixes.push('Added double quotes to unquoted keys');
    }

    // 6. Remove trailing commas before } or ]
    const trailingCommaRegex = /,\s*([\]}])/g;
    if (trailingCommaRegex.test(text)) {
      text = text.replace(trailingCommaRegex, '$1');
      fixes.push('Stripped trailing commas');
    }

    // 7. Test parse
    try {
      const parsed = JSON.parse(text);
      return { success: true, fixedText: JSON.stringify(parsed, null, 2), fixes };
    } catch (err) {
      return { success: false, error: err.message, fixedText: text, fixes };
    }
  }

  /* ─────────────────────────────────────────────────────────────────
     PANEL CONTROLLER CLASS
     ───────────────────────────────────────────────────────────────── */
  class JSONEditorPanel {
    constructor(id, initialData) {
      this.id = id; // 'left' or 'right'
      this.mode = 'code'; // 'code' | 'tree' | 'table'
      this.raw = typeof initialData === 'string' ? initialData : JSON.stringify(initialData, null, 2);
      this.parsed = null;
      this.isValid = true;
      this.indentSize = 2;
      this.history = [];
      this.historyIdx = -1;
      this.diffMap = null; // Map of path -> 'add'|'del'|'mod'

      // DOM Elements
      this.el = document.getElementById(`jePanel${capitalize(id)}`);
      this.textarea = this.el.querySelector('.je-textarea');
      this.gutter = this.el.querySelector('.je-line-gutter');
      this.sizePill = this.el.querySelector('.je-size-pill');
      this.statusIndicator = this.el.querySelector('.je-status-indicator');
      this.posIndicator = this.el.querySelector('.je-pos-indicator');
      this.codeWrapper = this.el.querySelector('.je-code-wrapper');
      this.treeWrapper = this.el.querySelector('.je-tree-wrapper');
      this.tableWrapper = this.el.querySelector('.je-table-wrapper');
      this.searchBar = this.el.querySelector('.je-search-bar');
      this.searchInput = this.el.querySelector('.je-search-input');
      this.searchCount = this.el.querySelector('.je-search-count');

      this.initEvents();
      this.pushHistory(this.raw);
      this.update(true);
    }

    initEvents() {
      // Textarea input
      this.textarea.addEventListener('input', () => {
        this.raw = this.textarea.value;
        this.pushHistory(this.raw);
        this.update(false);
      });

      // Cursor position tracking
      this.textarea.addEventListener('keyup', () => this.updateCursorPos());
      this.textarea.addEventListener('click', () => this.updateCursorPos());

      // Tab key interception & auto-indent
      this.textarea.addEventListener('keydown', (e) => {
        if (e.key === 'Tab') {
          e.preventDefault();
          const start = this.textarea.selectionStart;
          const end = this.textarea.selectionEnd;
          const spaces = ' '.repeat(this.indentSize);
          this.textarea.value = this.textarea.value.substring(0, start) + spaces + this.textarea.value.substring(end);
          this.textarea.selectionStart = this.textarea.selectionEnd = start + spaces.length;
          this.raw = this.textarea.value;
          this.update(false);
        } else if (e.key === 'z' && (e.ctrlKey || e.metaKey)) {
          if (e.shiftKey) {
            e.preventDefault();
            this.redo();
          } else {
            e.preventDefault();
            this.undo();
          }
        }
      });

      // Synchronize gutter scroll with textarea
      this.textarea.addEventListener('scroll', () => {
        this.gutter.scrollTop = this.textarea.scrollTop;
      });

      // Mode tab buttons
      const tabs = this.el.querySelectorAll('.je-mode-tab');
      tabs.forEach(tab => {
        tab.addEventListener('click', () => {
          const newMode = tab.dataset.mode;
          this.setMode(newMode);
        });
      });

      // Panel Action buttons
      this.bindAction('btnFormat', () => this.format());
      this.bindAction('btnMinify', () => this.minify());
      this.bindAction('btnRepair', () => this.repair());
      this.bindAction('btnCopy', () => this.copyToClipboard());
      this.bindAction('btnSearch', () => this.toggleSearch());
      this.bindAction('btnClear', () => this.clear());
      this.bindAction('btnExpandAll', () => this.expandAllTree(true));
      this.bindAction('btnCollapseAll', () => this.expandAllTree(false));

      // Search bar events
      if (this.searchInput) {
        this.searchInput.addEventListener('input', () => this.performSearch());
        this.searchInput.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            this.findNextMatch(e.shiftKey ? -1 : 1);
          } else if (e.key === 'Escape') {
            this.searchBar.hidden = true;
          }
        });
      }
      const closeSearch = this.el.querySelector('.je-search-close');
      if (closeSearch) {
        closeSearch.addEventListener('click', () => {
          this.searchBar.hidden = true;
        });
      }
      const btnNext = this.el.querySelector('.je-search-next');
      const btnPrev = this.el.querySelector('.je-search-prev');
      if (btnNext) btnNext.addEventListener('click', () => this.findNextMatch(1));
      if (btnPrev) btnPrev.addEventListener('click', () => this.findNextMatch(-1));
    }

    bindAction(className, handler) {
      const btn = this.el.querySelector(`.${className}`);
      if (btn) btn.addEventListener('click', handler);
    }

    pushHistory(val) {
      if (this.history[this.historyIdx] === val) return;
      this.history = this.history.slice(0, this.historyIdx + 1);
      this.history.push(val);
      if (this.history.length > 50) this.history.shift();
      this.historyIdx = this.history.length - 1;
    }

    undo() {
      if (this.historyIdx > 0) {
        this.historyIdx--;
        this.raw = this.history[this.historyIdx];
        this.textarea.value = this.raw;
        this.update(true);
        showToast('Undo', '↩');
      }
    }

    redo() {
      if (this.historyIdx < this.history.length - 1) {
        this.historyIdx++;
        this.raw = this.history[this.historyIdx];
        this.textarea.value = this.raw;
        this.update(true);
        showToast('Redo', '↪');
      }
    }

    update(syncTextarea = true) {
      if (syncTextarea) {
        this.textarea.value = this.raw;
      }

      // Size calculation
      const bytes = new Blob([this.raw]).size;
      this.sizePill.textContent = bytes > 1024 ? `${(bytes / 1024).toFixed(1)} KB` : `${bytes} B`;

      // Syntax parsing
      try {
        if (this.raw.trim() === '') {
          this.parsed = null;
          this.isValid = true;
          this.setStatus(true, 'Empty JSON');
        } else {
          this.parsed = JSON.parse(this.raw);
          this.isValid = true;
          const count = Array.isArray(this.parsed) ? `${this.parsed.length} items` : `${Object.keys(this.parsed).length} keys`;
          this.setStatus(true, `Valid JSON (${count})`);
        }
        this.renderGutters(null);
      } catch (err) {
        this.isValid = false;
        this.parsed = null;
        this.setStatus(false, err.message);
        this.highlightErrorLine(err.message);
      }

      // Re-render active view if not code
      if (this.mode === 'tree') {
        this.renderTree();
      } else if (this.mode === 'table') {
        this.renderTable();
      }

      this.updateCursorPos();
    }

    setStatus(valid, message) {
      if (valid) {
        this.statusIndicator.className = 'je-status-indicator valid';
        this.statusIndicator.innerHTML = `✓ ${message}`;
      } else {
        this.statusIndicator.className = 'je-status-indicator invalid';
        this.statusIndicator.innerHTML = `✕ Syntax Error: ${message}`;
      }
    }

    updateCursorPos() {
      if (!this.posIndicator) return;
      const pos = this.textarea.selectionStart || 0;
      const text = this.textarea.value.substring(0, pos);
      const lines = text.split('\n');
      const line = lines.length;
      const col = lines[lines.length - 1].length + 1;
      this.posIndicator.textContent = `Ln ${line}, Col ${col}`;
    }

    renderGutters(errorLine) {
      const lines = this.raw.split('\n').length;
      let html = '';
      for (let i = 1; i <= lines; i++) {
        const isErr = errorLine === i;
        html += `<div class="gutter-line ${isErr ? 'has-error' : ''}">${i}</div>`;
      }
      this.gutter.innerHTML = html;
    }

    highlightErrorLine(msg) {
      // Extract line number from error like "at position 45 (line 3 column 5)"
      const match = msg.match(/line (\d+)/i);
      const lineNum = match ? parseInt(match[1], 10) : null;
      this.renderGutters(lineNum);
    }

    setMode(mode) {
      if (mode === this.mode) return;
      if ((mode === 'tree' || mode === 'table') && !this.isValid) {
        showToast('Fix JSON syntax errors before switching views', '⚠️');
        return;
      }

      this.mode = mode;
      const tabs = this.el.querySelectorAll('.je-mode-tab');
      tabs.forEach(t => t.classList.toggle('active', t.dataset.mode === mode));

      this.codeWrapper.hidden = mode !== 'code';
      this.treeWrapper.hidden = mode !== 'tree';
      this.tableWrapper.hidden = mode !== 'table';

      if (mode === 'tree') {
        this.renderTree();
      } else if (mode === 'table') {
        this.renderTable();
      } else if (mode === 'code') {
        this.update(true);
      }
    }

    format() {
      try {
        if (!this.raw.trim()) return;
        const obj = JSON.parse(this.raw);
        this.raw = JSON.stringify(obj, null, this.indentSize);
        this.pushHistory(this.raw);
        this.update(true);
        showToast('JSON Formatted & Beautified', '✨');
        window.Analytics?.trackToolAction('json_editor', 'format', { char_length: this.raw.length });
      } catch (err) {
        showToast('Invalid JSON: Cannot format', '✕');
        window.Analytics?.trackError('json_editor', 'format_error', err.message);
      }
    }

    minify() {
      try {
        if (!this.raw.trim()) return;
        const obj = JSON.parse(this.raw);
        this.raw = JSON.stringify(obj);
        this.pushHistory(this.raw);
        this.update(true);
        showToast('JSON Minified / Compacted', '⚡');
        window.Analytics?.trackToolAction('json_editor', 'minify', { char_length: this.raw.length });
      } catch (err) {
        showToast('Invalid JSON: Cannot minify', '✕');
        window.Analytics?.trackError('json_editor', 'minify_error', err.message);
      }
    }

    repair() {
      const res = repairJSON(this.raw);
      if (res.success) {
        this.raw = res.fixedText;
        this.pushHistory(this.raw);
        this.update(true);
        const fixSummary = res.fixes.length ? res.fixes.join(', ') : 'Syntax corrected';
        showToast(`Repaired: ${fixSummary}`, '🛠️');
        window.Analytics?.trackToolAction('json_editor', 'repair', { success: true, fixes_count: res.fixes.length });
      } else {
        showToast(`Could not auto-repair: ${res.error}`, '✕');
        window.Analytics?.trackError('json_editor', 'repair_error', res.error);
      }
    }

    copyToClipboard() {
      navigator.clipboard.writeText(this.raw).then(() => {
        showToast('Copied to Clipboard!', '📋');
        window.Analytics?.trackCopy('json_data', 'json_editor', this.raw.length);
      }).catch(() => {
        showToast('Failed to copy', '✕');
      });
    }

    clear() {
      this.raw = '';
      this.pushHistory(this.raw);
      this.update(true);
      showToast('Cleared editor', '🗑️');
      window.Analytics?.trackToolAction('json_editor', 'clear');
    }

    toggleSearch() {
      if (this.searchBar) {
        this.searchBar.hidden = !this.searchBar.hidden;
        if (!this.searchBar.hidden) {
          this.searchInput.focus();
          this.performSearch();
        }
      }
    }

    performSearch() {
      const q = this.searchInput.value;
      if (!q) {
        this.searchCount.textContent = '0 matches';
        return;
      }
      const regex = new RegExp(q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
      const matches = this.raw.match(regex);
      const count = matches ? matches.length : 0;
      this.searchCount.textContent = `${count} match${count === 1 ? '' : 'es'}`;
    }

    findNextMatch(direction = 1) {
      const q = this.searchInput.value;
      if (!q) return;
      const text = this.textarea.value;
      const currentPos = this.textarea.selectionStart || 0;
      let nextPos = -1;

      if (direction === 1) {
        nextPos = text.toLowerCase().indexOf(q.toLowerCase(), currentPos + 1);
        if (nextPos === -1) nextPos = text.toLowerCase().indexOf(q.toLowerCase(), 0);
      } else {
        nextPos = text.toLowerCase().lastIndexOf(q.toLowerCase(), currentPos - 1);
        if (nextPos === -1) nextPos = text.toLowerCase().lastIndexOf(q.toLowerCase());
      }

      if (nextPos !== -1) {
        this.textarea.focus();
        this.textarea.setSelectionRange(nextPos, nextPos + q.length);
        this.updateCursorPos();
      }
    }

    /* ─── TREE VIEW RENDERER ─── */
    renderTree() {
      if (!this.parsed && this.raw.trim() !== '') return;
      this.treeWrapper.innerHTML = '';

      if (this.raw.trim() === '') {
        this.treeWrapper.innerHTML = '<div style="color: var(--text-muted); padding: 1rem;">Document is empty. Enter JSON in Code mode.</div>';
        return;
      }

      // Root tree container
      const rootNode = this.buildTreeNode('', this.parsed, '', true);
      this.treeWrapper.appendChild(rootNode);
    }

    buildTreeNode(key, value, path, isRoot = false) {
      const row = document.createElement('div');
      row.className = 'je-tree-item';
      row.dataset.path = path;

      const type = Array.isArray(value) ? 'array' : (value === null ? 'null' : typeof value);
      const isContainer = type === 'object' || type === 'array';

      // Diff check
      if (this.diffMap && this.diffMap.has(path)) {
        const diffType = this.diffMap.get(path);
        if (diffType === 'add') row.classList.add('diff-row-added');
        else if (diffType === 'del') row.classList.add('diff-row-removed');
        else if (diffType === 'mod') row.classList.add('diff-row-modified');
      }

      const header = document.createElement('div');
      header.className = 'je-tree-row';

      // Expander arrow
      const expander = document.createElement('span');
      expander.className = `je-tree-expander ${isContainer ? '' : 'empty'}`;
      expander.textContent = isContainer ? '▼' : '';
      header.appendChild(expander);

      // Key label (if not root)
      if (!isRoot) {
        const keySpan = document.createElement('span');
        keySpan.className = 'je-tree-key';
        keySpan.textContent = isNaN(key) ? `"${key}"` : key;
        keySpan.title = 'Click to edit key';
        keySpan.addEventListener('click', (e) => {
          e.stopPropagation();
          this.editTreeKey(path, keySpan);
        });
        header.appendChild(keySpan);

        const colon = document.createElement('span');
        colon.className = 'je-tree-colon';
        colon.textContent = ':';
        header.appendChild(colon);
      }

      // Value or container summary
      if (isContainer) {
        const badge = document.createElement('span');
        badge.className = 'je-tree-badge';
        const count = type === 'array' ? value.length : Object.keys(value).length;
        badge.textContent = type === 'array' ? `[ ${count} items ]` : `{ ${count} props }`;
        header.appendChild(badge);
      } else {
        const valSpan = document.createElement('span');
        valSpan.className = `je-tree-val val-${type}`;
        valSpan.textContent = type === 'string' ? `"${value}"` : String(value);
        valSpan.title = 'Click to edit value';
        valSpan.addEventListener('click', (e) => {
          e.stopPropagation();
          this.editTreeValue(path, valSpan, type);
        });
        header.appendChild(valSpan);
      }

      // Action buttons on hover
      const actions = document.createElement('span');
      actions.className = 'je-tree-actions';

      if (isContainer) {
        const btnAdd = document.createElement('button');
        btnAdd.className = 'je-node-btn';
        btnAdd.textContent = '+ Add';
        btnAdd.title = 'Insert child node';
        btnAdd.addEventListener('click', (e) => {
          e.stopPropagation();
          this.insertChildNode(path, type);
        });
        actions.appendChild(btnAdd);
      }

      if (!isRoot) {
        const btnDel = document.createElement('button');
        btnDel.className = 'je-node-btn del';
        btnDel.textContent = '✕';
        btnDel.title = 'Delete node';
        btnDel.addEventListener('click', (e) => {
          e.stopPropagation();
          this.deleteNode(path);
        });
        actions.appendChild(btnDel);
      }

      header.appendChild(actions);
      row.appendChild(header);

      // Child container if object/array
      if (isContainer) {
        const children = document.createElement('div');
        children.className = 'je-tree-children';
        children.style.paddingLeft = '1.4rem';

        const keys = Object.keys(value);
        keys.forEach(k => {
          const childPath = path ? `${path}.${k}` : k;
          const childNode = this.buildTreeNode(k, value[k], childPath, false);
          children.appendChild(childNode);
        });

        row.appendChild(children);

        // Toggle collapsible
        expander.addEventListener('click', (e) => {
          e.stopPropagation();
          const isCollapsed = expander.classList.toggle('collapsed');
          children.hidden = isCollapsed;
        });
      }

      return row;
    }

    editTreeKey(path, elem) {
      const oldKey = elem.textContent.replace(/^"|"$/g, '');
      const input = document.createElement('input');
      input.type = 'text';
      input.value = oldKey;
      input.className = 'je-search-input';
      input.style.width = '110px';
      input.style.display = 'inline-block';

      const commit = () => {
        const newKey = input.value.trim();
        if (newKey && newKey !== oldKey) {
          this.renameKeyAtPath(path, newKey);
        } else {
          elem.textContent = isNaN(oldKey) ? `"${oldKey}"` : oldKey;
        }
      };

      input.addEventListener('blur', commit);
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') elem.textContent = `"${oldKey}"`;
      });

      elem.textContent = '';
      elem.appendChild(input);
      input.focus();
      input.select();
    }

    editTreeValue(path, elem, currentType) {
      const rawVal = elem.textContent.replace(/^"|"$/g, '');
      const input = document.createElement('input');
      input.type = 'text';
      input.value = rawVal;
      input.className = 'je-search-input';
      input.style.width = '140px';
      input.style.display = 'inline-block';

      const commit = () => {
        let typedVal = input.value;
        if (currentType === 'number') typedVal = Number(typedVal);
        else if (currentType === 'boolean') typedVal = typedVal === 'true';
        else if (currentType === 'null') typedVal = null;
        this.setValueAtPath(path, typedVal);
      };

      input.addEventListener('blur', commit);
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') this.renderTree();
      });

      elem.textContent = '';
      elem.appendChild(input);
      input.focus();
      input.select();
    }

    setValueAtPath(path, newVal) {
      const parts = path.split('.');
      let cur = this.parsed;
      for (let i = 0; i < parts.length - 1; i++) {
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = newVal;
      this.raw = JSON.stringify(this.parsed, null, this.indentSize);
      this.pushHistory(this.raw);
      this.update(true);
      showToast('Updated tree node', '✓');
    }

    renameKeyAtPath(path, newKey) {
      const parts = path.split('.');
      const oldKey = parts[parts.length - 1];
      let cur = this.parsed;
      for (let i = 0; i < parts.length - 1; i++) {
        cur = cur[parts[i]];
      }
      if (Array.isArray(cur)) return;

      const newObj = {};
      Object.keys(cur).forEach(k => {
        if (k === oldKey) {
          newObj[newKey] = cur[oldKey];
        } else {
          newObj[k] = cur[k];
        }
      });
      // Replace properties
      for (let k in cur) delete cur[k];
      Object.assign(cur, newObj);

      this.raw = JSON.stringify(this.parsed, null, this.indentSize);
      this.pushHistory(this.raw);
      this.update(true);
      showToast(`Renamed key to "${newKey}"`, '✓');
    }

    insertChildNode(path, containerType) {
      let target = this.parsed;
      if (path) {
        const parts = path.split('.');
        parts.forEach(p => target = target[p]);
      }

      if (containerType === 'array') {
        target.push("new_item");
      } else {
        const baseKey = "newKey";
        let k = baseKey;
        let c = 1;
        while (target.hasOwnProperty(k)) {
          k = `${baseKey}_${c++}`;
        }
        target[k] = "new_value";
      }

      this.raw = JSON.stringify(this.parsed, null, this.indentSize);
      this.pushHistory(this.raw);
      this.update(true);
      showToast('Inserted new item', '➕');
    }

    deleteNode(path) {
      const parts = path.split('.');
      const last = parts.pop();
      let cur = this.parsed;
      parts.forEach(p => cur = cur[p]);

      if (Array.isArray(cur)) {
        cur.splice(Number(last), 1);
      } else {
        delete cur[last];
      }

      this.raw = JSON.stringify(this.parsed, null, this.indentSize);
      this.pushHistory(this.raw);
      this.update(true);
      showToast('Node removed', '🗑️');
    }

    expandAllTree(expand = true) {
      const expanders = this.treeWrapper.querySelectorAll('.je-tree-expander:not(.empty)');
      expanders.forEach(exp => {
        const children = exp.closest('.je-tree-item').querySelector('.je-tree-children');
        if (children) {
          exp.classList.toggle('collapsed', !expand);
          children.hidden = !expand;
        }
      });
    }

    /* ─── TABLE VIEW RENDERER ─── */
    renderTable() {
      if (!this.parsed) return;
      this.tableWrapper.innerHTML = '';

      let rows = [];
      let isObjectOfObjects = false;

      if (Array.isArray(this.parsed)) {
        rows = this.parsed;
      } else if (typeof this.parsed === 'object' && this.parsed !== null) {
        // Test if keys hold objects
        const keys = Object.keys(this.parsed);
        if (keys.length > 0 && typeof this.parsed[keys[0]] === 'object') {
          isObjectOfObjects = true;
          rows = keys.map(k => Object.assign({ _key: k }, this.parsed[k]));
        } else {
          // Key-value table
          rows = keys.map(k => ({ Key: k, Value: this.parsed[k] }));
        }
      }

      if (!rows.length) {
        this.tableWrapper.innerHTML = '<div style="padding: 1.5rem; color: var(--text-muted);">No tabular data detected. Table view is designed for arrays of objects.</div>';
        return;
      }

      // Collect all unique columns
      const colsSet = new Set();
      rows.forEach(r => {
        if (typeof r === 'object' && r !== null) {
          Object.keys(r).forEach(k => colsSet.add(k));
        }
      });
      const cols = Array.from(colsSet);

      const table = document.createElement('table');
      const thead = document.createElement('thead');
      const headerRow = document.createElement('tr');

      const thIdx = document.createElement('th');
      thIdx.className = 'je-table-index';
      thIdx.textContent = '#';
      headerRow.appendChild(thIdx);

      cols.forEach(col => {
        const th = document.createElement('th');
        th.textContent = col;
        th.title = 'Click to sort column';
        th.addEventListener('click', () => this.sortTable(col));
        headerRow.appendChild(th);
      });
      thead.appendChild(headerRow);
      table.appendChild(thead);

      const tbody = document.createElement('tbody');
      rows.forEach((row, i) => {
        const tr = document.createElement('tr');
        const tdIdx = document.createElement('td');
        tdIdx.className = 'je-table-index';
        tdIdx.textContent = i + 1;
        tr.appendChild(tdIdx);

        cols.forEach(col => {
          const td = document.createElement('td');
          const val = row[col];
          td.textContent = typeof val === 'object' ? JSON.stringify(val) : String(val === undefined ? '' : val);
          td.title = 'Double click to edit cell';
          td.addEventListener('dblclick', () => {
            this.editTableCell(i, col, td);
          });
          tr.appendChild(td);
        });
        tbody.appendChild(tr);
      });

      table.appendChild(tbody);
      this.tableWrapper.appendChild(table);
    }

    sortTable(col) {
      if (!Array.isArray(this.parsed)) return;
      this.sortAsc = !this.sortAsc;
      this.parsed.sort((a, b) => {
        const valA = a[col] || '';
        const valB = b[col] || '';
        if (valA < valB) return this.sortAsc ? -1 : 1;
        if (valA > valB) return this.sortAsc ? 1 : -1;
        return 0;
      });
      this.raw = JSON.stringify(this.parsed, null, this.indentSize);
      this.pushHistory(this.raw);
      this.update(true);
      showToast(`Sorted by ${col} (${this.sortAsc ? 'ASC' : 'DESC'})`, '⇅');
    }

    editTableCell(rowIndex, col, tdElem) {
      const currentVal = tdElem.textContent;
      const input = document.createElement('input');
      input.className = 'je-search-input';
      input.value = currentVal;
      input.style.width = '100%';

      const commit = () => {
        let val = input.value;
        if (!isNaN(val) && val.trim() !== '') val = Number(val);
        else if (val === 'true') val = true;
        else if (val === 'false') val = false;
        else if (val === 'null') val = null;

        if (Array.isArray(this.parsed)) {
          this.parsed[rowIndex][col] = val;
        }
        this.raw = JSON.stringify(this.parsed, null, this.indentSize);
        this.pushHistory(this.raw);
        this.update(true);
      };

      input.addEventListener('blur', commit);
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') this.renderTable();
      });

      tdElem.textContent = '';
      tdElem.appendChild(input);
      input.focus();
      input.select();
    }
  }

  /* ─────────────────────────────────────────────────────────────────
     JSON COMPARISON / DIFF ENGINE
     ───────────────────────────────────────────────────────────────── */
  function computeJSONDiff(obj1, obj2) {
    const diffs = {
      leftDiffs: new Map(), // path -> 'del' | 'mod'
      rightDiffs: new Map(), // path -> 'add' | 'mod'
      countAdd: 0,
      countDel: 0,
      countMod: 0
    };

    function compare(val1, val2, path) {
      const type1 = Array.isArray(val1) ? 'array' : (val1 === null ? 'null' : typeof val1);
      const type2 = Array.isArray(val2) ? 'array' : (val2 === null ? 'null' : typeof val2);

      if (type1 !== type2) {
        diffs.leftDiffs.set(path, 'mod');
        diffs.rightDiffs.set(path, 'mod');
        diffs.countMod++;
        return;
      }

      if (type1 === 'object') {
        const keys1 = Object.keys(val1);
        const keys2 = Object.keys(val2);
        const allKeys = new Set([...keys1, ...keys2]);

        allKeys.forEach(k => {
          const subPath = path ? `${path}.${k}` : k;
          if (!val1.hasOwnProperty(k)) {
            diffs.rightDiffs.set(subPath, 'add');
            diffs.countAdd++;
          } else if (!val2.hasOwnProperty(k)) {
            diffs.leftDiffs.set(subPath, 'del');
            diffs.countDel++;
          } else {
            compare(val1[k], val2[k], subPath);
          }
        });
      } else if (type1 === 'array') {
        const maxLen = Math.max(val1.length, val2.length);
        for (let i = 0; i < maxLen; i++) {
          const subPath = path ? `${path}.${i}` : `${i}`;
          if (i >= val1.length) {
            diffs.rightDiffs.set(subPath, 'add');
            diffs.countAdd++;
          } else if (i >= val2.length) {
            diffs.leftDiffs.set(subPath, 'del');
            diffs.countDel++;
          } else {
            compare(val1[i], val2[i], subPath);
          }
        }
      } else {
        if (val1 !== val2) {
          diffs.leftDiffs.set(path, 'mod');
          diffs.rightDiffs.set(path, 'mod');
          diffs.countMod++;
        }
      }
    }

    compare(obj1, obj2, '');
    return diffs;
  }

  /* ─────────────────────────────────────────────────────────────────
     CSV CONVERTERS
     ───────────────────────────────────────────────────────────────── */
  function jsonToCSV(jsonArray) {
    if (!Array.isArray(jsonArray) || jsonArray.length === 0) return '';
    const headers = Object.keys(jsonArray[0]);
    const csvRows = [headers.join(',')];
    jsonArray.forEach(row => {
      const values = headers.map(h => {
        let val = row[h];
        if (val === undefined || val === null) val = '';
        else if (typeof val === 'object') val = JSON.stringify(val);
        val = String(val).replace(/"/g, '""');
        return `"${val}"`;
      });
      csvRows.push(values.join(','));
    });
    return csvRows.join('\n');
  }

  function csvToJSON(csvText) {
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) return [];
    const headers = lines[0].split(',').map(h => h.replace(/^["']|["']$/g, '').trim());
    const result = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.replace(/^["']|["']$/g, '').trim());
      const obj = {};
      headers.forEach((h, idx) => {
        let val = values[idx] || '';
        if (!isNaN(val) && val !== '') val = Number(val);
        else if (val === 'true') val = true;
        else if (val === 'false') val = false;
        obj[h] = val;
      });
      result.push(obj);
    }
    return result;
  }

  function capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  /* ─────────────────────────────────────────────────────────────────
     WORKSPACE SUITE MANAGER
     ───────────────────────────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', () => {
    // Check URL query parameters or hash for initial data
    let initialLeft = SAMPLES.users;
    let initialRight = SAMPLES.products;

    try {
      const hash = window.location.hash;
      if (hash.startsWith('#data=')) {
        const decoded = decodeURIComponent(hash.substring(6));
        initialLeft = JSON.parse(decoded);
      }
    } catch (e) {}

    const panelLeft = new JSONEditorPanel('left', initialLeft);
    const panelRight = new JSONEditorPanel('right', initialRight);

    // Workspace container
    const workspace = document.getElementById('jeWorkspace');
    const btnDualToggle = document.getElementById('btnDualToggle');

    let isDual = true;
    if (btnDualToggle) {
      btnDualToggle.addEventListener('click', () => {
        isDual = !isDual;
        workspace.classList.toggle('single-pane', !isDual);
        btnDualToggle.innerHTML = isDual ? '<span>◫</span> Single Pane' : '<span>◫</span> Dual Pane';
        showToast(isDual ? 'Switched to Dual Workspace' : 'Switched to Single Workspace', '◫');
      });
    }

    // ─── MIDDLE TOOLBAR CONTROLS ───
    const btnCopyLeftToRight = document.getElementById('btnCopyLeftToRight');
    const btnCopyRightToLeft = document.getElementById('btnCopyRightToLeft');
    const btnCompareDiff = document.getElementById('btnCompareDiff');
    const btnSwapPanels = document.getElementById('btnSwapPanels');
    const diffBanner = document.getElementById('jeDiffBanner');

    if (btnCopyLeftToRight) {
      btnCopyLeftToRight.addEventListener('click', () => {
        panelRight.raw = panelLeft.raw;
        panelRight.pushHistory(panelRight.raw);
        panelRight.update(true);
        showToast('Copied Left → Right', '→');
      });
    }

    if (btnCopyRightToLeft) {
      btnCopyRightToLeft.addEventListener('click', () => {
        panelLeft.raw = panelRight.raw;
        panelLeft.pushHistory(panelLeft.raw);
        panelLeft.update(true);
        showToast('Copied Right → Left', '←');
      });
    }

    if (btnSwapPanels) {
      btnSwapPanels.addEventListener('click', () => {
        const temp = panelLeft.raw;
        panelLeft.raw = panelRight.raw;
        panelRight.raw = temp;
        panelLeft.pushHistory(panelLeft.raw);
        panelRight.pushHistory(panelRight.raw);
        panelLeft.update(true);
        panelRight.update(true);
        showToast('Swapped documents', '⇄');
      });
    }

    if (btnCompareDiff) {
      btnCompareDiff.addEventListener('click', () => {
        if (!panelLeft.isValid || !panelRight.isValid) {
          showToast('Both documents must be valid JSON to compare', '⚠️');
          return;
        }

        const diffs = computeJSONDiff(panelLeft.parsed, panelRight.parsed);
        panelLeft.diffMap = diffs.leftDiffs;
        panelRight.diffMap = diffs.rightDiffs;

        // Force tree mode for visual comparison
        panelLeft.setMode('tree');
        panelRight.setMode('tree');

        if (diffBanner) {
          diffBanner.hidden = false;
          diffBanner.innerHTML = `
            <div>
              <strong>Diff Comparison:</strong>
              <span class="je-diff-tag add">+ ${diffs.countAdd} Additions</span>
              <span class="je-diff-tag del">- ${diffs.countDel} Deletions</span>
              <span class="je-diff-tag mod">≠ ${diffs.countMod} Modifications</span>
            </div>
            <button class="je-btn je-btn-ghost je-btn-sm" id="btnCloseDiff">Close Diff ✕</button>
          `;
          document.getElementById('btnCloseDiff').addEventListener('click', () => {
            diffBanner.hidden = true;
            panelLeft.diffMap = null;
            panelRight.diffMap = null;
            panelLeft.renderTree();
            panelRight.renderTree();
          });
        }
        showToast(`Differences detected: ${diffs.countAdd + diffs.countDel + diffs.countMod}`, '⚖️');
      });
    }

    // ─── TOP GLOBAL ACTIONS ───
    const sampleSelect = document.getElementById('jeSampleSelect');
    if (sampleSelect) {
      sampleSelect.addEventListener('change', (e) => {
        const val = e.target.value;
        if (SAMPLES[val]) {
          panelLeft.raw = JSON.stringify(SAMPLES[val], null, panelLeft.indentSize);
          panelLeft.pushHistory(panelLeft.raw);
          panelLeft.update(true);
          showToast(`Loaded sample: ${e.target.options[e.target.selectedIndex].text}`, '📂');
          window.Analytics?.track('preset_select', { tool_name: 'json_editor', preset_name: val });
        }
      });
    }

    let currentFileName = 'document.json';

    // Load file from disk
    const fileInput = document.getElementById('jeFileInput');
    const btnOpenFile = document.getElementById('btnOpenFile');
    if (btnOpenFile && fileInput) {
      btnOpenFile.addEventListener('click', () => fileInput.click());
      fileInput.addEventListener('change', (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const baseName = file.name.replace(/\.[^/.]+$/, '');
        currentFileName = `${baseName}.json`;
        const reader = new FileReader();
        reader.onload = (event) => {
          let content = event.target.result;
          if (file.name.endsWith('.csv')) {
            try {
              content = JSON.stringify(csvToJSON(content), null, 2);
              showToast('CSV converted to JSON', '📊');
            } catch (err) {}
          }
          panelLeft.raw = content;
          panelLeft.pushHistory(panelLeft.raw);
          panelLeft.update(true);
          showToast(`Opened: ${file.name}`, '📂');
        };
        reader.readAsText(file);
      });
    }

    // Download active file
    const btnDownload = document.getElementById('btnDownload');
    if (btnDownload) {
      btnDownload.addEventListener('click', () => {
        let exportName = currentFileName || 'document.json';
        if (!exportName.toLowerCase().endsWith('.json')) {
          exportName += '.json';
        }
        const blob = new Blob([panelLeft.raw], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = exportName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        showToast(`JSON downloaded: ${exportName}`, '💾');
      });
    }

    // Drag and Drop files onto workspace
    workspace.addEventListener('dragover', (e) => {
      e.preventDefault();
      workspace.style.outline = '2px dashed var(--accent-rent)';
    });
    workspace.addEventListener('dragleave', () => {
      workspace.style.outline = 'none';
    });
    workspace.addEventListener('drop', (e) => {
      e.preventDefault();
      workspace.style.outline = 'none';
      const file = e.dataTransfer.files[0];
      if (file) {
        const baseName = file.name.replace(/\.[^/.]+$/, '');
        currentFileName = `${baseName}.json`;
        const reader = new FileReader();
        reader.onload = (event) => {
          panelLeft.raw = event.target.result;
          panelLeft.pushHistory(panelLeft.raw);
          panelLeft.update(true);
          showToast(`Loaded ${file.name} via drag-and-drop`, '📥');
        };
        reader.readAsText(file);
      }
    });

    // ─── QUERY / TRANSFORM MODAL ───
    const transformModal = document.getElementById('jeTransformModal');
    const transformPreset = document.getElementById('jeTransformPreset');
    const transformQuery = document.getElementById('jeTransformQuery');
    const transformPreview = document.getElementById('jeTransformPreview');
    const btnApplyTransform = document.getElementById('btnApplyTransform');
    const btnCloseTransform = document.getElementById('btnCloseTransform');
    let activeTransformPanel = panelLeft;

    function openTransform(panel) {
      activeTransformPanel = panel;
      if (!panel.isValid || !panel.parsed) {
        showToast('Ensure document is valid JSON before transforming', '⚠️');
        return;
      }
      transformModal.hidden = false;
      updateTransformPreview();
    }

    // Attach transform buttons on panels
    const btnTransformLeft = document.getElementById('btnTransformLeft');
    const btnTransformRight = document.getElementById('btnTransformRight');
    if (btnTransformLeft) btnTransformLeft.addEventListener('click', () => openTransform(panelLeft));
    if (btnTransformRight) btnTransformRight.addEventListener('click', () => openTransform(panelRight));

    if (transformPreset) {
      transformPreset.addEventListener('change', () => {
        const val = transformPreset.value;
        if (val === 'filter') {
          transformQuery.value = 'data.filter(item => item.id > 101 || item.price > 200)';
        } else if (val === 'map') {
          transformQuery.value = 'data.map(item => ({ name: item.name, key: item.id || item.sku }))';
        } else if (val === 'sort') {
          transformQuery.value = 'data.sort((a, b) => (a.name > b.name ? 1 : -1))';
        } else if (val === 'keys') {
          transformQuery.value = 'Object.keys(data)';
        }
        updateTransformPreview();
      });
    }

    function updateTransformPreview() {
      try {
        const fn = new Function('data', `return (${transformQuery.value});`);
        const result = fn(activeTransformPanel.parsed);
        transformPreview.value = JSON.stringify(result, null, 2);
      } catch (err) {
        transformPreview.value = `// Transform Error: ${err.message}`;
      }
    }

    if (transformQuery) {
      transformQuery.addEventListener('input', updateTransformPreview);
    }

    if (btnApplyTransform) {
      btnApplyTransform.addEventListener('click', () => {
        try {
          const fn = new Function('data', `return (${transformQuery.value});`);
          const result = fn(activeTransformPanel.parsed);
          activeTransformPanel.raw = JSON.stringify(result, null, activeTransformPanel.indentSize);
          activeTransformPanel.pushHistory(activeTransformPanel.raw);
          activeTransformPanel.update(true);
          transformModal.hidden = true;
          showToast('Transformation applied successfully', '⚡');
        } catch (err) {
          showToast(`Error: ${err.message}`, '✕');
        }
      });
    }

    if (btnCloseTransform) {
      btnCloseTransform.addEventListener('click', () => {
        transformModal.hidden = true;
      });
    }

    // ─── CSV CONVERTER MODAL ───
    const csvModal = document.getElementById('jeCsvModal');
    const btnOpenCsvModal = document.getElementById('btnOpenCsvModal');
    const btnCloseCsvModal = document.getElementById('btnCloseCsvModal');
    const btnExportCsvNow = document.getElementById('btnExportCsvNow');

    if (btnOpenCsvModal && csvModal) {
      btnOpenCsvModal.addEventListener('click', () => {
        csvModal.hidden = false;
      });
    }
    if (btnCloseCsvModal && csvModal) {
      btnCloseCsvModal.addEventListener('click', () => {
        csvModal.hidden = true;
      });
    }
    if (btnExportCsvNow) {
      btnExportCsvNow.addEventListener('click', () => {
        if (!panelLeft.isValid || !Array.isArray(panelLeft.parsed)) {
          showToast('JSON must be an array of objects to export to CSV', '⚠️');
          return;
        }
        const csvStr = jsonToCSV(panelLeft.parsed);
        const blob = new Blob([csvStr], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `json-export-${new Date().toISOString().slice(0,10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        csvModal.hidden = true;
        showToast('CSV downloaded successfully', '📁');
      });
    }

    // Share link button
    const btnShare = document.getElementById('btnShare');
    if (btnShare) {
      btnShare.addEventListener('click', () => {
        try {
          const encoded = encodeURIComponent(panelLeft.raw);
          const url = `${window.location.origin}${window.location.pathname}#data=${encoded}`;
          navigator.clipboard.writeText(url).then(() => {
            showToast('Shareable URL copied to clipboard!', '🔗');
          });
        } catch (err) {
          showToast('Unable to create share URL (data too large)', '⚠️');
        }
      });
    }
  });

})();
